import React, { useState, useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { isAdmin, isAdminEmail } from '../utils/admin';
import { formatDateTime, formatDateOnly, formatTimeOnly, formatExpireDateShort } from '../utils/date';

const cellStyle = { padding: '0.75rem 1rem' };
const tableHeaderStyle = { textAlign: 'left', ...cellStyle };

const AdminPanel = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [checkInUserId, setCheckInUserId] = useState('');
  const [checkInSlotId, setCheckInSlotId] = useState('');
  const [checkInSubmitting, setCheckInSubmitting] = useState(false);
  const [userListFilterDate, setUserListFilterDate] = useState('');
  const [creditAmounts, setCreditAmounts] = useState({});
  const [setExpiryValues, setSetExpiryValues] = useState({});
  const location = useLocation();
  const navigate = useNavigate();
  const hash = (location.hash || '').replace('#', '');
  const activeSection = ['users', 'packages', 'classes', 'checkin', 'emails'].includes(hash) ? hash : 'users';

  const sections = [
    { id: 'users', label: 'Users' },
    { id: 'packages', label: 'Credits & expiry' },
    { id: 'classes', label: 'Upcoming Classes' },
    { id: 'checkin', label: 'Check in user' },
    { id: 'emails', label: 'Download emails' },
  ];

  const fetchData = async (dateFilter = null) => {
    const date = dateFilter !== undefined ? dateFilter : userListFilterDate;
    try {
      setError('');
      const [usersRes, slotsRes] = await Promise.all([
        api.get('/admin/users', { params: date ? { date } : {} }),
        api.get('/timeslots').catch(() => ({ data: [] })),
      ]);
      setUsers(usersRes.data.users || []);
      setSlots(Array.isArray(slotsRes.data) ? slotsRes.data : []);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Admin access required.');
      } else {
        setError(err.response?.data?.error || err.message || 'Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    fetchData();
  }, [user]);

  const setSection = (id) => {
    navigate(`/admin#${id}`, { replace: true });
  };

  const handleDownloadEmails = async () => {
    try {
      const res = await api.get('/admin/users/emails', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'user-emails.txt';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to download emails');
    }
  };

  const handleGiveCredits = async (userId, amount) => {
    const amt = parseInt(amount, 10);
    if (!Number.isInteger(amt) || amt < 1) {
      alert('Please enter a positive number of credits.');
      return;
    }
    const u = users.find((x) => x.id === userId);
    const name = u ? (u.name || u.email) : 'this user';
    if (!window.confirm(`Add ${amt} credit(s) to ${name}?`)) {
      return;
    }
    try {
      await api.post(`/admin/users/${userId}/credits`, { amount: amt });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to add credits');
    }
  };

  const handleSetExpiry = async (userId, packageExpiresAt) => {
    if (!packageExpiresAt || !packageExpiresAt.trim()) {
      alert('Please enter a date (YYYY-MM-DD).');
      return;
    }
    const u = users.find((x) => x.id === userId);
    const name = u ? (u.name || u.email) : 'this user';
    if (!window.confirm(`Set package expiry to ${packageExpiresAt} for ${name}?`)) {
      return;
    }
    try {
      await api.post(`/admin/users/${userId}/set-expiry`, { package_expires_at: packageExpiresAt.trim() });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to set expiry');
    }
  };

  const handleExtendPackage = async (u) => {
    const weeksStr = window.prompt(`Extend package by how many weeks for ${u.name || u.email}?`, '2');
    if (weeksStr == null || weeksStr.trim() === '') return;
    const weeks = parseInt(weeksStr.trim(), 10);
    if (!Number.isInteger(weeks) || weeks < 1) {
      alert('Please enter a positive number of weeks.');
      return;
    }
    try {
      await api.post(`/admin/users/${u.id}/extend-package`, { weeks });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to extend package');
    }
  };

  const handleDeleteUser = async (u) => {
    const name = u.name || u.email || 'this user';
    if (!window.confirm(`Permanently delete user ${name} (${u.email})? This cannot be undone.`)) {
      return;
    }
    try {
      await api.delete(`/admin/users/${u.id}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleAdminCheckIn = async (e) => {
    e.preventDefault();
    if (!checkInUserId || !checkInSlotId) {
      alert('Select a user and a class.');
      return;
    }
    setCheckInSubmitting(true);
    try {
      await api.post(`/timeslots/${checkInSlotId}/admin-checkin`, { user_id: parseInt(checkInUserId, 10) });
      alert('User checked in successfully.');
      setCheckInUserId('');
      setCheckInSlotId('');
      fetchData();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to check in user');
    } finally {
      setCheckInSubmitting(false);
    }
  };


  const upcomingSlots = slots.filter((s) => s.start_time && new Date(s.start_time).getTime() > Date.now());

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (loading) {
    return <div className="container">Loading admin panel...</div>;
  }

  if (error && !isAdmin(user)) {
    return (
      <div className="container">
        <div style={{ color: 'var(--danger, #dc3545)', marginBottom: '1rem' }}>{error}</div>
        <p>You do not have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div className="container">
      <h1>Admin Panel</h1>
      {error && <div style={{ color: 'var(--danger, #dc3545)', marginBottom: '1rem' }}>{error}</div>}

      <div style={{ display: 'flex', gap: '8px', marginTop: '2rem', marginBottom: '3.5rem', flexWrap: 'wrap' }}>
        {sections.map((s) => (
          <button
            key={s.id}
            type="button"
            className={activeSection === s.id ? 'btn btn-primary' : 'btn btn-secondary'}
            onClick={() => setSection(s.id)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* 1. User list (no credits/extend here) */}
      {activeSection === 'users' && (
      <section id="users">
        <h2 style={{ marginBottom: '1.25rem' }}>User list</h2>
        <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <label style={{ fontWeight: 500 }}>Filter by class date:</label>
          <input
            type="date"
            value={userListFilterDate}
            onChange={(e) => {
              const v = e.target.value;
              setUserListFilterDate(v);
              fetchData(v || null);
            }}
            style={{ padding: '6px 10px' }}
          />
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => {
              setUserListFilterDate('');
              fetchData(null);
            }}
          >
            Show all users
          </button>
          {userListFilterDate && (
            <span style={{ fontSize: '14px', color: '#666' }}>
              Showing users who checked in to a class on this date
            </span>
          )}
        </div>
        <div className="card" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border, #ddd)' }}>
                <th style={tableHeaderStyle}>Email</th>
                <th style={tableHeaderStyle}>Name</th>
                <th style={tableHeaderStyle}>Phone</th>
                <th style={tableHeaderStyle}>Credits</th>
                <th style={tableHeaderStyle}>Policy acknowledged</th>
                <th style={tableHeaderStyle}>Classes finished (total)</th>
                <th style={tableHeaderStyle}>Package expires</th>
                <th style={tableHeaderStyle}>Delete</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} style={{ borderBottom: '1px solid var(--border, #eee)' }}>
                  <td style={cellStyle}>{u.email}</td>
                  <td style={cellStyle}>{u.name || '—'}</td>
                  <td style={cellStyle}>{u.phone || '—'}</td>
                  <td style={cellStyle}>{u.credits ?? 0}</td>
                  <td style={cellStyle}>
                    {isAdminEmail(u.email)
                      ? '—'
                      : u.policy_acknowledged_at
                        ? formatDateTime(u.policy_acknowledged_at)
                        : 'No'}
                  </td>
                  <td style={cellStyle}>{u.total_checkins ?? 0}</td>
                  <td style={cellStyle}>
                    {u.package_expires_at
                      ? formatExpireDateShort(u.package_expires_at)
                      : '—'}
                  </td>
                  <td style={cellStyle}>
                    {!isAdminEmail(u.email) && u.id !== user?.id ? (
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => handleDeleteUser(u)}
                      >
                        Delete
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      )}

      {/* 2. Credits & expiry: give credits, extend package */}
      {activeSection === 'packages' && (
      <section id="packages">
        <h2 style={{ marginBottom: '1.25rem' }}>Credits & expiry</h2>
        <p style={{ marginBottom: '1rem', color: 'var(--textSecondary, #666)' }}>
          Add any number of credits. Set or extend package expiry date separately in the columns to the right.
        </p>
        <div style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <label style={{ fontWeight: 500 }}>Filter by class date:</label>
          <input
            type="date"
            value={userListFilterDate}
            onChange={(e) => {
              const v = e.target.value;
              setUserListFilterDate(v);
              fetchData(v || null);
            }}
            style={{ padding: '6px 10px' }}
          />
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={() => {
              setUserListFilterDate('');
              fetchData(null);
            }}
          >
            Show all users
          </button>
        </div>
        <div className="card" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border, #ddd)' }}>
                <th style={tableHeaderStyle}>Email</th>
                <th style={tableHeaderStyle}>Name</th>
                <th style={tableHeaderStyle}>Credits</th>
                <th style={tableHeaderStyle}>Package expires</th>
                <th style={tableHeaderStyle}>Add credits</th>
                <th style={tableHeaderStyle}>Set expiry date</th>
                <th style={tableHeaderStyle}>Extend by weeks</th>
              </tr>
            </thead>
            <tbody>
              {users.filter((u) => !isAdminEmail(u.email)).map((u) => (
                <tr key={u.id} style={{ borderBottom: '1px solid var(--border, #eee)' }}>
                  <td style={cellStyle}>{u.email}</td>
                  <td style={cellStyle}>{u.name || '—'}</td>
                  <td style={cellStyle}>{u.credits ?? 0}</td>
                  <td style={cellStyle}>
                    {u.package_expires_at
                      ? formatExpireDateShort(u.package_expires_at)
                      : '—'}
                  </td>
                  <td style={cellStyle}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input
                        type="number"
                        min={1}
                        max={99}
                        value={creditAmounts[u.id] ?? 4}
                        onChange={(e) => setCreditAmounts((prev) => ({ ...prev, [u.id]: e.target.value === '' ? '' : Math.max(1, parseInt(e.target.value, 10) || 1) }))}
                        style={{ width: 52, padding: '4px 6px' }}
                        title="Number of credits"
                      />
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={() => handleGiveCredits(u.id, creditAmounts[u.id] ?? 4)}
                      >
                        Add credits
                      </button>
                    </div>
                  </td>
                  <td style={cellStyle}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input
                        type="date"
                        value={setExpiryValues[u.id] ?? ''}
                        onChange={(e) => setSetExpiryValues((prev) => ({ ...prev, [u.id]: e.target.value }))}
                        style={{ padding: '4px 6px' }}
                        title="Set expiry date"
                      />
                      <button
                        type="button"
                        className="btn btn-success btn-sm"
                        onClick={() => handleSetExpiry(u.id, setExpiryValues[u.id])}
                        disabled={!setExpiryValues[u.id]}
                      >
                        Set expiry
                      </button>
                    </div>
                  </td>
                  <td style={cellStyle}>
                    {u.package_expires_at ? (
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleExtendPackage(u)}
                      >
                        Extend by n weeks
                      </button>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
      )}

      {/* 3. Class list */}
      {activeSection === 'classes' && (
      <section id="classes">
        <h2 style={{ marginBottom: '1.25rem' }}>Class list</h2>
        <div className="card" style={{ overflowX: 'auto' }}>
          {upcomingSlots.length === 0 ? (
            <p style={{ padding: '1rem', color: 'var(--textSecondary, #666)' }}>No upcoming classes.</p>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border, #ddd)' }}>
                  <th style={tableHeaderStyle}>Title</th>
                  <th style={tableHeaderStyle}>Date</th>
                  <th style={tableHeaderStyle}>Time</th>
                  <th style={tableHeaderStyle}>Participants</th>
                </tr>
              </thead>
              <tbody>
                {upcomingSlots.map((s) => (
                  <tr key={s.id} style={{ borderBottom: '1px solid var(--border, #eee)' }}>
                    <td style={cellStyle}>{s.title}</td>
                    <td style={cellStyle}>{formatDateOnly(s.start_time)}</td>
                    <td style={cellStyle}>{formatTimeOnly(s.start_time)} – {formatTimeOnly(s.end_time)}</td>
                    <td style={cellStyle}>{s.current_participants ?? 0} / {s.max_participants ?? 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
      )}

      {/* 4. Check in user for a class */}
      {activeSection === 'checkin' && (
      <section id="checkin">
        <h2 style={{ marginBottom: '1.25rem' }}>Check in user for a class</h2>
        <div className="card" style={{ maxWidth: '500px' }}>
          <form onSubmit={handleAdminCheckIn}>
            <div className="form-group">
              <label>User</label>
              <select
                value={checkInUserId}
                onChange={(e) => setCheckInUserId(e.target.value)}
                required
                style={{ width: '100%', padding: '8px' }}
              >
                <option value="">Select user</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>{u.name || u.email} ({u.email})</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Class</label>
              <select
                value={checkInSlotId}
                onChange={(e) => setCheckInSlotId(e.target.value)}
                required
                style={{ width: '100%', padding: '8px' }}
              >
                <option value="">Select class</option>
                {slots.map((s) => {
                  const isPast = s.start_time && new Date(s.start_time).getTime() <= Date.now();
                  const isFull = (s.current_participants ?? 0) >= (s.max_participants ?? 0);
                  return (
                    <option key={s.id} value={s.id} disabled={isFull || isPast}>
                      {s.title} – {formatDateOnly(s.start_time)} ({s.current_participants ?? 0}/{s.max_participants ?? 0}){isPast ? ' (passed)' : ''}
                    </option>
                  );
                })}
              </select>
            </div>
            <button type="submit" className="btn btn-primary" disabled={checkInSubmitting}>
              {checkInSubmitting ? 'Checking in…' : 'Check in user'}
            </button>
          </form>
        </div>
      </section>
      )}

      {/* 5. Download user emails */}
      {activeSection === 'emails' && (
      <section id="emails">
        <h2 style={{ marginBottom: '1.25rem' }}>Download user emails</h2>
        <div className="card">
          <p style={{ marginBottom: '1rem', color: 'var(--textSecondary, #666)' }}>
            Download a text file with one email per line.
          </p>
          <button type="button" className="btn btn-primary" onClick={handleDownloadEmails}>
            Download emails
          </button>
        </div>
      </section>
      )}
    </div>
  );
};

export default AdminPanel;
